import { isTextModel, isVoiceModel, modelKind } from './modelKinds';
import { useSeriesAssets } from './assetCatalog';
import { StageGuide } from './StageGuide';
import { rememberRecovery, readRecovery, forgetRecovery } from './productionRecovery';
import { requestedEpisode, remoteDraftAction, sameDraft } from './workflowUiState';
import { SpeechLines, VoicePicker, speechMatchesShot, type SpeechLine } from './Speech';
import { ShotPlanView, ShotPlanEditor, ArtDirectionEditor } from './ShotPlan';
import { syncShot, shotPrompt } from './shotPlanData';
import type { CharacterImagePrompt, SourceEvidence } from './CharacterPromptBuilder';
import { audioDirection, dialogueMode } from './dialogueMode';
import {
  useCharacterReferences,
  castForShot,
  ReferenceSelection,
  type CharacterReference,
} from './CharacterReferences';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Film,
  Plus,
  ArrowUp,
  ArrowDown,
  Trash2,
  Upload,
  Sparkles,
  Play,
  Download,
  Check,
  Save,
  Volume2,
  ImagePlus,
  WandSparkles,
  CircleCheck,
  RefreshCw,
} from 'lucide-react';
import { api, uploadAsset, type Page, type Schema } from '../api/client';
import { useStudio } from './App';
import { mediaFailureMessage } from './mediaErrors';
import { useApi, Dialog, Field, Badge, Empty, Spinner } from './shared';
import {
  CharacterVoicePanel,
  useCharacterVoices,
  voiceSummary,
  type CharacterVoice,
} from './CharacterVoices';

type Model = Schema<'ModelSummary'>;
export type Asset = {
  sizeBytes?: string;
  id: string;
  name: string;
  mimeType: string;
  url: string;
  sha256: string;
  spec: {
    durationSeconds: number;
    width?: number;
    height?: number;
    hasAudio: boolean;
    origin: string;
    timingValid?: boolean;
    speechLineOnly?: boolean;
    speechPreviewOnly?: boolean;
  };
};
type Shot = Schema<'ProductionShot'>;
type Draft = {
  sceneDesigns?: Schema<'SceneDesign'>[];
  propDesigns?: Schema<'PropDesign'>[];
  title: string;
  script: string;
  visualStyle: string;
  shots: Shot[];
  handoff: { summary: string; characterStates: string; openThreads: string; nextHook: string };
  musicId: string;
  musicVolume: number;
  previousReleaseId?: string;
};
type Cut = { id: string; revision: string; hash: string; body: Draft; createdAt: string };
type Release = {
  id: string;
  episodeId: string;
  cutId: string;
  asset: Asset;
  handoff: Draft['handoff'];
  createdAt: string;
};
type Document = {
  episodeId: string;
  revision: string;
  hash: string;
  body: Draft;
  format: Schema<'TargetFormat'>;
  cuts: Cut[];
  releases: Release[];
  previousRelease: Release | null;
};
export type StudioTask = {
  referenceImages?: CharacterReference[];
  referenceMode?: string;
  canRecover?: boolean;
  id: string;
  episodeId?: string;
  seriesId?: string;
  branchId?: string;
  characterId?: string;
  artifactKind?: string;
  kind: string;
  state: string;
  revision: string;
  progress: number;
  step?: string;
  result: {
    speechPartial?: boolean;
    speechLines?: SpeechLine[];
    warnings?: string[];
    timingValid?: boolean;
    characterImagePrompt?: CharacterImagePrompt;
    sourceEvidence?: SourceEvidence;
    asset?: Asset;
    proposal?: Draft;
    sourceDocumentHash?: string;
    visualReview?: {
      summary: string;
      findings: {
        severity: 'info' | 'warning' | 'error';
        description: string;
        suggestion: string;
        sampleIndices: number[];
      }[];
    };
    reviewedAsset?: Asset;
    samples?: {
      index: number;
      atSeconds: number | null;
      width: number;
      height: number;
      sha256: string;
      url: string;
    }[];
    technical?: {
      decode: string;
      durationSeconds: number;
      sampleCount: number;
      audioReviewed: boolean;
    };
    model?: { upstreamModelId: string };
    continuityReview?: {
      summary: string;
      findings: {
        severity: 'info' | 'warning' | 'error';
        category: string;
        description: string;
        suggestion: string;
        sourceIds: string[];
      }[];
    };
    sources?: { id: string; label: string; status: string; content: unknown }[];
    limitations?: string[];
    designProposal?: {
      artifactKind: string;
      scope: { seriesId: string; branchId: string; characterId?: string; episodeId?: string };
      content: Record<string, unknown>;
      model: { upstreamModelId: string };
    };
    text?: string;
    qa?: { decode: string; actualSeconds: number };
  };
  errorCode?: string;
  errorMessage?: string;
  shotId?: string;
  cutId?: string;
  modelId?: string;
  createdAt: string;
  speechPreview?: { text: string; voice: string; language: string; modelName?: string };
};
type Settings = { callsEnabled: boolean };
const names: Record<string, string> = {
  probe: '连接验证',
  write: '剧本与分镜',
  storyboard: '分镜设计',
  design: 'AI 创作设计',
  review: '连续性审查',
  qa: '画面抽样审查',
  image: '生成图片',
  video: '生成视频',
  music: '生成音乐',
  voice: '角色配音',
  render: '合成成片',
  queued: '等待执行',
  running: '正在执行',
  completed: '已完成',
  failed: '失败',
  outcome_unknown: '结果待核对',
  cancelled: '已取消',
};
const pending = (t: StudioTask) => ['queued', 'running'].includes(t.state);
export function useTasks(path: string) {
  return useQuery({
    queryKey: ['api', path],
    queryFn: () => api<Page<StudioTask>>(path),
    refetchInterval: (q) => (q.state.data?.items.some(pending) ? 2000 : 8000),
  });
}
export function ModelSetupButton({ model }: { model: Model }) {
  const { base, perform, busy } = useStudio();
  const [open, setOpen] = useState(false);
  const settings = useApi<Settings>(`${base}/model-settings`),
    tasks = useTasks(`${base}/studio-tasks`);
  const task = tasks.data?.items.find(
    (t) => t.modelId === model.catalogModelId && t.kind === 'probe',
  );
  const qc = useQueryClient();
  const last = useRef('');
  useEffect(() => {
    if (task && !pending(task) && last.current !== task.id + task.state) {
      last.current = task.id + task.state;
      void qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'api' &&
          typeof q.queryKey[1] === 'string' &&
          (q.queryKey[1].startsWith(`${base}/models`) ||
            q.queryKey[1].startsWith(`${base}/connections`) ||
            q.queryKey[1].endsWith('/model-policy')),
      });
    }
  }, [task?.id, task?.state, base, qc]);
  const isMedia = modelKind(model) !== 'text';
  return (
    <>
      <button
        className="button small secondary"
        onClick={() => {
          setOpen(true);
        }}
      >
        {task && pending(task) ? '正在测试…' : model.selectable ? '管理' : '配置与测试'}
      </button>
      {open && (
        <Dialog
          title={model.displayName}
          subtitle="使用真实请求验证当前连接"
          close={() => setOpen(false)}
        >
          <div className="dialog-body">
            {!settings.data?.callsEnabled && (
              <div className="info-box">服务端模型调用开关尚未开启。</div>
            )}
            {isMedia ? (
              <div className="info-box">
                {isVoiceModel(model)
                  ? '语音模型可在角色库生成中文试听、绑定角色音色，也可在制作页逐镜生成独立配音。试听和素材会自动保存。'
                  : '媒体模型在“制作与成片”里生成实际素材；生成结果将保留在素材库。'}
              </div>
            ) : (
              <button
                className="button primary"
                disabled={busy || !settings.data?.callsEnabled || (!!task && pending(task))}
                onClick={() =>
                  void perform(
                    () =>
                      api(`${base}/studio-tasks`, 'POST', {
                        kind: 'probe',
                        modelId: model.catalogModelId,
                        confirmed: true,
                      }),
                    '测试请求已加入队列',
                  )
                }
              >
                <Sparkles size={15} />
                发送测试请求
              </button>
            )}
            {task && (
              <div className="probe-result">
                <Badge tone={task.state === 'completed' ? 'green' : 'neutral'}>
                  {names[task.state]}
                </Badge>
                <p>
                  {task.result.text ??
                    task.errorMessage ??
                    '验证中文应答和 JSON 返回格式，通过后自动启用文字模型。'}
                </p>
                {task.canRecover && (
                  <button
                    className="button secondary"
                    disabled={busy}
                    onClick={() =>
                      void perform(
                        () => api(`${base}/studio-tasks/${task.id}/recover`, 'POST', {}),
                        '正在恢复原请求结果',
                      )
                    }
                  >
                    恢复原结果
                  </button>
                )}
                {task.state === 'completed' && (
                  <small>一次样本验证通过；不代表所有功能或持续可用性已验证。</small>
                )}
              </div>
            )}
          </div>
        </Dialog>
      )}
    </>
  );
}

export function ProductionPage() {
  const { base, project, episodes, episodesLoading, perform, busy, toast, refresh, go } =
    useStudio();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeEpisodeId = searchParams.get('episode') ?? '';
  const [episodeId, setEpisodeId] = useState(searchParams.get('episode') ?? ''),
    [tab, setTab] = useState('script'),
    [draft, setDraft] = useState<Draft | null>(null),
    [loadedRevision, setLoadedRevision] = useState('0');
  const [savedBody, setSavedBody] = useState<Draft | null>(null);
  const [remoteUpdate, setRemoteUpdate] = useState<Document | null>(null);
  useEffect(() => setEpisodeId(routeEpisodeId), [routeEpisodeId]);
  const [generator, setGenerator] = useState<{
      kind: string;
      episodeId?: string;
      seconds?: number;
      shotId?: string | undefined;
      assetId?: string | undefined;
      prompt: string;
      firstFrameStepId?: string | undefined;
      voice?: CharacterVoice | undefined;
      referenceRoles?: CharacterReference[] | undefined;
      referenceIds?: string[] | undefined;
    } | null>(null),
    [approval, setApproval] = useState(false),
    [reviewed, setReviewed] = useState(false),
    [publish, setPublish] = useState<StudioTask | null>(null),
    [selectedAsset, setSelectedAsset] = useState<Asset | null>(null),
    [voiceEditor, setVoiceEditor] = useState<string | null>(null);
  const [draftEpisode, setDraftEpisode] = useState(''),
    [recovered, setRecovered] = useState<Draft | null>(null);
  const current = requestedEpisode(episodes, episodeId),
    id = current?.id;
  const voices = useCharacterVoices(project?.id, current?.branchId);
  const references = useCharacterReferences(project?.id, current?.branchId);
  const doc = useApi<Document>(`${base}/episodes/${id}/production`, !!id);
  const assets = useSeriesAssets(
    project?.id,
    draft && draftEpisode === id
      ? [draft.musicId, ...draft.shots.flatMap((shot) => [shot.visualId, shot.voiceId])]
      : [],
  );
  const tasks = useTasks(`${base}/studio-tasks${id ? '?episodeId=' + id : ''}`);
  const flows = useQuery({
    queryKey: ['api', `${base}/novel-workflows?seriesId=${project?.id}`],
    queryFn: () =>
      api<
        Page<{
          id: string;
          episodeId?: string;
          branchId: string;
          stage: string;
          state: string;
          paused: boolean;
          episodeNumber: number;
          settings: { visualMode: string };
        }>
      >(`${base}/novel-workflows?seriesId=${project?.id}`),
    enabled: !!project?.id,
    refetchInterval: 5000,
  });
  useEffect(() => {
    if (doc.data && doc.data.episodeId === id) {
      const action = remoteDraftAction(
        draft && savedBody && draftEpisode
          ? { episodeId: draftEpisode, revision: loadedRevision, base: savedBody, draft }
          : null,
        doc.data,
      );
      if (action === 'unchanged') return;
      if (action === 'conflict') {
        setRemoteUpdate(doc.data);
        return;
      }
      let initial = structuredClone(doc.data.body);
      if (draftEpisode !== doc.data.episodeId)
        try {
          const retained = readRecovery<Draft>(sessionStorage, doc.data.episodeId);
          setRecovered(retained && Array.isArray(retained.shots) ? retained : null);
          const cached = JSON.parse(
            sessionStorage.getItem('dream.production.' + doc.data.episodeId) ?? 'null',
          );
          if (cached?.body && Array.isArray(cached.body.shots)) {
            if (cached.revision === doc.data.revision) initial = cached.body;
            else {
              try {
                rememberRecovery(sessionStorage, doc.data.episodeId, cached.body);
                setRecovered(cached.body);
              } catch {
                initial = cached.body;
              }
            }
          }
        } catch {}
      setDraft(initial);
      setSavedBody(structuredClone(doc.data.body));
      setRemoteUpdate(null);
      setDraftEpisode(doc.data.episodeId);
      setLoadedRevision(doc.data.revision);
    }
  }, [doc.data, id, draftEpisode, loadedRevision, savedBody, draft]);
  const observed = useRef('');
  useEffect(() => {
    const key = tasks.data?.items.map((t) => t.id + t.state).join() ?? '';
    if (key && key !== observed.current) {
      observed.current = key;
      void assets.refetch();
      void doc.refetch();
    }
  }, [tasks.data]);
  const dirty = !!draft && !!savedBody && !sameDraft(draft, savedBody);
  useEffect(() => {
    if (!draft || draftEpisode !== id) return;
    try {
      if (dirty)
        sessionStorage.setItem(
          'dream.production.' + id,
          JSON.stringify({ revision: loadedRevision, body: draft }),
        );
      else sessionStorage.removeItem('dream.production.' + id);
    } catch {}
  }, [draft, draftEpisode, id, dirty, loadedRevision]);
  useEffect(() => {
    const fn = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', fn);
    return () => window.removeEventListener('beforeunload', fn);
  }, [dirty]);
  if (!project)
    return (
      <Empty
        title="先选择一个系列"
        text="制作与成片会保存到所选系列。"
        action={
          <button className="button primary" onClick={() => go('projects')}>
            选择系列
          </button>
        }
      />
    );
  if (!current && episodesLoading)
    return (
      <div className="page">
        <Spinner />
        正在读取指定分支的剧集…
      </div>
    );
  if (!current && episodeId)
    return (
      <Empty
        title="没有找到指定剧集"
        text="该剧集不在当前分支，或已被删除。没有替你打开其他剧集，请回小说流程重新进入或查看剧集列表。"
        action={
          <button className="button secondary" onClick={() => go('episodes')}>
            查看当前分支剧集
          </button>
        }
      />
    );
  if (!current)
    return (
      <Empty
        icon={<Film />}
        title="从第一集开始"
        text="创建剧集后，在这里编写剧本、安排镜头，并合成为一分钟短剧。"
        action={
          <button className="button primary" onClick={() => go('episodes')}>
            创建第一集
          </button>
        }
      />
    );
  if (!draft || !doc.data || draftEpisode !== id)
    return (
      <div className="page">
        <Spinner />
        {doc.isError && <p>{doc.error.message}</p>}
      </div>
    );
  const fps = doc.data.format.fpsNumerator / doc.data.format.fpsDenominator;
  const seconds = draft.shots.reduce((sum, s) => sum + s.durationFrames, 0) / fps;
  const activeCut = doc.data.cuts.find((c) => c.hash === doc.data!.hash);
  const release = doc.data.releases.find((r) => r.episodeId === id);
  const resultTasks = tasks.data?.items ?? [];
  const currentFlow = flows.data?.items.find(
    (flow) => flow.episodeId === id && flow.branchId === current.branchId,
  );
  const patch = (v: Partial<Draft>) => setDraft({ ...draft, ...v });
  const shotPatch = (shotId: string, v: Partial<Shot>) => {
    let clearedVoice = false;
    const shots = draft.shots.map((shot) => {
      if (shot.id !== shotId) return shot;
      const next = syncShot({ ...shot, ...v });
      const speechChanged = (
        ['dialogueLines', 'dialogue', 'speaker', 'durationFrames', 'dialogueMode'] as const
      ).some((key) => JSON.stringify(shot[key]) !== JSON.stringify(next[key]));
      if (shot.voiceId && speechChanged) {
        next.voiceId = '';
        clearedVoice = true;
      }
      return next;
    });
    patch({ shots });
    if (clearedVoice)
      toast('台词或时间已改变，已取消旧配音的选用；音频仍保留，保存后请生成新配音。');
  };
  const save = async () => {
    if (remoteUpdate) throw new Error('服务器已有更新，请先选择保留本地草稿或载入服务器版本。');
    const data = await api<Document>(`${base}/episodes/${id}/production`, 'PUT', {
      expectedRevision: loadedRevision,
      body: draft,
    });
    setDraft(structuredClone(data.body));
    setSavedBody(structuredClone(data.body));
    setLoadedRevision(data.revision);
    await refresh();
  };
  const generate = async (kind: string, shot?: Shot) => {
    if (remoteUpdate) {
      toast('服务器已有新版制作稿，请先处理版本冲突再生成。', true);
      return;
    }
    if (['video', 'voice'].includes(kind) && dirty) {
      toast('请先保存分镜修改，再生成画面或配音', true);
      return;
    }
    if (kind === 'video' && (references.isPending || references.isError)) {
      toast('角色参考图尚未载入，请稍后再试', true);
      return;
    }
    if (kind === 'video' && shot?.dialogue.trim() && (voices.isPending || voices.isError)) {
      toast('角色声音尚未载入，请稍后再试', true);
      return;
    }
    let prepared: Schema<'VideoPromptPreview'> | undefined;
    if (kind === 'video' && shot) {
      try {
        prepared = await api<Schema<'VideoPromptPreview'>>(
          `${base}/novel-workflows/video-prompt?episodeId=${id}&shotId=${encodeURIComponent(shot.id)}`,
        );
      } catch (error) {
        toast((error as Error).message, true);
        return;
      }
    }
    setGenerator({
      kind,
      episodeId: id!,
      seconds: shot ? shot.durationFrames / fps : 10,
      ...(prepared?.available ? { firstFrameStepId: prepared.firstFrameStepId } : {}),
      ...(kind === 'video'
        ? {
            referenceRoles: references.data?.items ?? [],
            referenceIds: castForShot(references.data?.items ?? [], shot),
          }
        : {}),
      shotId: shot?.id,
      ...(kind === 'video' && shot?.dialogue.trim()
        ? { voice: voices.data?.items.find((v) => v.name === shot.speaker) }
        : {}),
      prompt:
        kind === 'write'
          ? draft.script || '请为这一集设计有冲突、有反转、有下集钩子的完整短剧。'
          : kind === 'storyboard'
            ? '依据已保存剧本安排镜头，保持对白和剧情，细化景别、动作和衔接，总长符合本集规格。'
            : kind === 'review'
              ? '检查人物动机、角色知情范围、前后集衔接和每分钟节奏。'
              : kind === 'music'
                ? '为这集短剧创作电影配乐，纯音乐，不要人声。'
                : shot
                  ? prepared?.available
                    ? prepared.prompt!
                    : shotPrompt(shot, draft.visualStyle, kind === 'video', fps, draft)
                  : '',
    });
  };
  return (
    <div className="page production-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">FROM YOUR STORY TO THE SCREEN</span>
          <h1>把这一分钟，拍出来。</h1>
          <p>剧本、画面和声音，汇成能继续讲下去的故事。</p>
        </div>
        <div className="production-actions">
          <select
            aria-label="制作剧集"
            value={id}
            disabled={dirty}
            onChange={(e) => {
              setEpisodeId(e.target.value);
              const params = new URLSearchParams(searchParams);
              params.set('episode', e.target.value);
              setSearchParams(params);
              setDraft(null);
            }}
          >
            {episodes.map((e) => (
              <option key={e.id} value={e.id}>
                第 {e.number} 集 · {e.title}
              </option>
            ))}
          </select>
          <button
            className="button primary"
            disabled={busy || !dirty || !!remoteUpdate}
            onClick={() => void perform(save, '制作草稿已保存')}
          >
            <Save size={16} />
            {dirty ? '保存草稿' : '已保存'}
          </button>
        </div>
      </div>
      {currentFlow && (
        <>
          <StageGuide
            stage={currentFlow.stage}
            state={currentFlow.state}
            paused={currentFlow.paused}
            visualMode={currentFlow.settings.visualMode}
            episodeNumber={currentFlow.episodeNumber}
          />
          <div className="info-box">
            制作页负责编辑本集内容；流程审批请回小说工作流。修改保存后，还需要在流程中同步本集制作稿。
            <button className="button small secondary" onClick={() => go('novel')}>
              返回流程审批
            </button>
          </div>
        </>
      )}
      {remoteUpdate && (
        <section className="info-box" role="alert">
          <div>
            <strong>服务器上的本集已更新到第 {remoteUpdate.revision} 版</strong>
            <p>
              你的修改仍保留在当前浏览器会话。载入服务器版本会暂存本地草稿，刷新后可恢复；选择保留本地稿不会立即写入，之后保存将以你的整份草稿替换服务器最新内容。
            </p>
          </div>
          <button
            className="button secondary"
            onClick={() => {
              try {
                rememberRecovery(sessionStorage, id!, draft);
              } catch {
                toast('浏览器无法暂存当前草稿，尚未切换版本。请先复制保存你的修改。', true);
                return;
              }
              setRecovered(structuredClone(draft));
              setDraft(structuredClone(remoteUpdate.body));
              setSavedBody(structuredClone(remoteUpdate.body));
              setLoadedRevision(remoteUpdate.revision);
              setRemoteUpdate(null);
            }}
          >
            载入服务器版本
          </button>
          <button
            className="button secondary"
            onClick={() => {
              setSavedBody(structuredClone(remoteUpdate.body));
              setLoadedRevision(remoteUpdate.revision);
              setRemoteUpdate(null);
              toast('已保留本地完整草稿；核对后保存才会替换服务器内容。');
            }}
          >
            保留本地完整草稿
          </button>
        </section>
      )}
      {recovered && (
        <div className="info-box">
          当前浏览器会话留有暂存的未保存草稿。{dirty ? '请先保存当前修改，再载入暂存稿。' : ''}
          <button
            className="button small secondary"
            disabled={dirty}
            onClick={() => {
              try {
                sessionStorage.setItem(
                  'dream.production.' + id,
                  JSON.stringify({ revision: loadedRevision, body: recovered }),
                );
                forgetRecovery(sessionStorage, id!);
              } catch {
                toast('浏览器无法保存恢复状态，暂存稿仍保留，请稍后再试。', true);
                return;
              }
              setDraft(recovered);
              setRecovered(null);
              toast('旧草稿已载入，请与最新内容核对后保存');
            }}
          >
            载入旧草稿审阅
          </button>
          <button
            className="text-button"
            onClick={() => {
              forgetRecovery(sessionStorage, id!);
              setRecovered(null);
            }}
          >
            丢弃暂存稿，保留当前内容
          </button>
        </div>
      )}
      {assets.error && (
        <p className="form-error" role="alert">
          素材读取失败：{assets.error.message}{' '}
          <button className="text-button" onClick={() => void assets.refetch()}>
            重新读取
          </button>
        </p>
      )}
      <div className="production-summary">
        <div>
          <Badge tone={release ? 'green' : activeCut && !dirty ? 'blue' : 'neutral'}>
            {release ? '已发布' : activeCut && !dirty ? '已定稿' : '制作草稿'}
          </Badge>
          <strong>
            {seconds.toFixed(1)} <small>/ {doc.data.format.targetSeconds} 秒</small>
          </strong>
          <span>
            {draft.shots.length} 个镜头 · {doc.data.format.width} × {doc.data.format.height}
          </span>
        </div>
        <span>{dirty ? '有未保存的修改' : `版本 ${loadedRevision}`}</span>
      </div>
      {doc.data.previousRelease && (
        <div className="continuity-strip">
          <CircleCheck size={18} />
          <div>
            <strong>前集交接已就绪</strong>
            <p>{doc.data.previousRelease.handoff.summary}</p>
          </div>
          {draft.previousReleaseId !== doc.data.previousRelease.id && (
            <button
              className="button small secondary"
              onClick={() => patch({ previousReleaseId: doc.data!.previousRelease!.id })}
            >
              载入前集承接
            </button>
          )}
        </div>
      )}
      {current.number > 1 && !doc.data.previousRelease && (
        <div className="info-box">
          前一集尚未发布。你可以先写草稿，发布前需要载入前集交接并审阅连续性。
        </div>
      )}
      <div className="tabs production-tabs">
        {[
          ['script', '剧本与承接'],
          ['shots', '分镜时间线'],
          ['assets', '素材与声音'],
          ['delivery', '成片与发布'],
        ].map(([key, label]) => (
          <button key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key!)}>
            {label}
          </button>
        ))}
      </div>
      <div className="production-layout">
        <section className="production-main">
          {tab === 'script' && (
            <>
              <div className="section-heading">
                <h2>这一集，发生什么？</h2>
                <div className="row-actions">
                  <button
                    className="button secondary"
                    disabled={dirty || busy}
                    onClick={() => generate('review')}
                  >
                    <Check size={15} />
                    连续性审查
                  </button>
                  <button
                    className="button primary"
                    disabled={dirty || busy}
                    onClick={() => generate('write')}
                  >
                    <WandSparkles size={15} />
                    AI 写剧本与分镜
                  </button>
                </div>
              </div>
              <Field label="本集标题">
                <input
                  value={draft.title}
                  maxLength={200}
                  onChange={(e) => patch({ title: e.target.value })}
                />
              </Field>
              <Field
                label="完整剧本 / 创作要求"
                hint="对白、动作和转折都可以写在这里。AI 生成的内容会先作为候选，由你决定是否采用。"
              >
                <textarea
                  className="script-input"
                  value={draft.script}
                  readOnly={draft.shots.some((s) => !!s.staging)}
                  maxLength={30000}
                  onChange={(e) => patch({ script: e.target.value })}
                  placeholder="第一幕：她在旧收音机里，听到了自己的求救声……"
                />
              </Field>
              <Field label="统一画面风格">
                <textarea
                  rows={3}
                  value={draft.visualStyle}
                  onChange={(e) => patch({ visualStyle: e.target.value })}
                />
              </Field>
              <h3>留给下一集的交接</h3>
              <div className="handoff-grid">
                {(
                  [
                    ['summary', '本集已经发生的事实'],
                    ['characterStates', '人物状态与各自知道的信息'],
                    ['openThreads', '未解决的伏笔'],
                    ['nextHook', '下一集开场钩子'],
                  ] as const
                ).map(([key, label]) => (
                  <Field label={label} key={key}>
                    <textarea
                      rows={4}
                      value={draft.handoff[key]}
                      maxLength={8000}
                      onChange={(e) =>
                        patch({ handoff: { ...draft.handoff, [key]: e.target.value } })
                      }
                    />
                  </Field>
                ))}
              </div>
            </>
          )}
          {tab === 'shots' && (
            <>
              {assets.hasNextPage && (
                <p className="quiet">
                  已绑定素材会直接读取；要选择更早的候选，可继续加载。
                  <button
                    className="button small secondary"
                    disabled={assets.isFetchingNextPage}
                    onClick={() => void assets.fetchNextPage()}
                  >
                    {assets.isFetchingNextPage ? '正在读取…' : '载入更早素材'}
                  </button>
                </p>
              )}
              <div className="section-heading">
                <h2>每一帧，都在推进故事。</h2>
                <button
                  className="button primary"
                  disabled={dirty || busy || !draft.script.trim()}
                  onClick={() => generate('storyboard')}
                  title={
                    dirty
                      ? '请先保存当前修改'
                      : !draft.script.trim()
                        ? '请先填写并保存剧本'
                        : '使用分镜角色所选模型'
                  }
                >
                  <WandSparkles size={15} />
                  AI 设计分镜
                </button>
                <button
                  className="button secondary"
                  onClick={() =>
                    patch({
                      shots: [
                        ...draft.shots,
                        {
                          id: crypto.randomUUID(),
                          description: '',
                          dialogue: '',
                          speaker: '',
                          durationFrames: Math.round(10 * fps),
                          visualId: '',
                          voiceId: '',
                          sourceInSeconds: 0,
                          voice: 'Tingting',
                          caption: '',
                        },
                      ],
                    })
                  }
                >
                  <Plus size={15} />
                  添加镜头
                </button>
              </div>
              <div className="timeline-ruler">
                {draft.shots.map((s, i) => (
                  <button
                    key={s.id}
                    style={{ flex: s.durationFrames }}
                    onClick={() =>
                      document
                        .getElementById('shot-' + s.id)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }
                  >
                    <span>{String(i + 1).padStart(2, '0')}</span>
                    <small>{(s.durationFrames / fps).toFixed(1)}s</small>
                  </button>
                ))}
              </div>
              {!draft.shots.length && (
                <Empty
                  title="先写出你的第一组镜头"
                  text="可以手动添加，也可以在剧本页让 AI 根据设定生成分镜。"
                />
              )}
              {draft.sceneDesigns?.length ? (
                <ArtDirectionEditor design={draft} change={patch} />
              ) : null}
              {draft.shots.map((shot, index) => {
                const visual = assets.data?.items.find((a) => a.id === shot.visualId);
                const audio = assets.data?.items.find((a) => a.id === shot.voiceId);
                return (
                  <article className="shot-editor" id={'shot-' + shot.id} key={shot.id}>
                    <header>
                      <span className="shot-number">{String(index + 1).padStart(2, '0')}</span>
                      <h3>{shot.speaker || '镜头'}</h3>
                      <div className="row-actions">
                        {[-1, 1].map((delta) => (
                          <button
                            className="icon-button"
                            key={delta}
                            aria-label={delta < 0 ? '上移镜头' : '下移镜头'}
                            disabled={index + delta < 0 || index + delta >= draft.shots.length}
                            onClick={() => {
                              const shots = [...draft.shots];
                              [shots[index], shots[index + delta]] = [
                                shots[index + delta]!,
                                shots[index]!,
                              ];
                              patch({ shots });
                            }}
                          >
                            {delta < 0 ? <ArrowUp size={15} /> : <ArrowDown size={15} />}
                          </button>
                        ))}
                        <button
                          className="icon-button"
                          aria-label="删除镜头"
                          onClick={() =>
                            patch({ shots: draft.shots.filter((s) => s.id !== shot.id) })
                          }
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </header>
                    <div className="shot-body">
                      <div className="shot-visual">
                        {visual ? (
                          <AssetPreview asset={visual} />
                        ) : (
                          <div className="shot-placeholder">
                            <Film size={30} />
                            <span>
                              {shot.visualId
                                ? assets.boundPending
                                  ? '正在读取已选画面…'
                                  : '已选素材暂不可用，请核对素材记录'
                                : '为这一幕选择画面'}
                            </span>
                          </div>
                        )}
                        <select
                          aria-label={`镜头 ${index + 1} 画面`}
                          value={shot.visualId}
                          onChange={(e) => shotPatch(shot.id, { visualId: e.target.value })}
                        >
                          <option value="">选择图片或视频</option>
                          {shot.visualId && !visual && (
                            <option value={shot.visualId} disabled>
                              {assets.boundPending ? '正在读取已选画面…' : '已选画面暂不可用'}
                            </option>
                          )}
                          {assets.data?.items
                            .filter((a) => /^(image|video)\//.test(a.mimeType))
                            .map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.name} ·{' '}
                                {a.spec.durationSeconds
                                  ? a.spec.durationSeconds.toFixed(1) + 's'
                                  : '图片'}
                              </option>
                            ))}
                        </select>
                        <div className="row-actions">
                          <button
                            className="button small secondary"
                            onClick={() => generate('image', shot)}
                          >
                            <ImagePlus size={14} />
                            生成图片
                          </button>
                          <button
                            className="button small secondary"
                            onClick={() => generate('video', shot)}
                          >
                            <Film size={14} />
                            生成视频
                          </button>
                        </div>
                      </div>
                      <div className="shot-fields">
                        <Field label="画面、动作与运镜">
                          <textarea
                            rows={3}
                            value={shot.description}
                            onChange={(e) => shotPatch(shot.id, { description: e.target.value })}
                          />
                        </Field>
                        <div className="shot-grid">
                          <Field label="时长（秒）">
                            <input
                              type="number"
                              min={1 / fps}
                              step={1 / fps}
                              max={120}
                              value={Math.round((shot.durationFrames / fps) * 1000) / 1000}
                              onChange={(e) =>
                                shotPatch(shot.id, {
                                  durationFrames: Math.max(
                                    1,
                                    Math.round(Number(e.target.value) * fps),
                                  ),
                                })
                              }
                            />
                          </Field>
                          <Field label="视频素材入点（秒）">
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={shot.sourceInSeconds}
                              onChange={(e) =>
                                shotPatch(shot.id, { sourceInSeconds: Number(e.target.value) })
                              }
                            />
                          </Field>
                          {!shot.staging && (
                            <Field label="说话角色">
                              <select
                                value={shot.speaker}
                                onChange={(e) => shotPatch(shot.id, { speaker: e.target.value })}
                              >
                                <option value="">旁白 / 未指定角色</option>
                                {shot.speaker &&
                                  !voices.data?.items.some((v) => v.name === shot.speaker) && (
                                    <option value={shot.speaker}>{shot.speaker}</option>
                                  )}
                                {voices.data?.items.map((v) => (
                                  <option key={v.characterId} value={v.name}>
                                    {v.name}
                                  </option>
                                ))}
                              </select>
                            </Field>
                          )}
                        </div>
                        {!shot.staging && (
                          <>
                            <Field
                              label="说话方式"
                              hint="角色的内心独白请选择画外旁白，不要求人物张嘴。"
                            >
                              <select
                                value={dialogueMode(shot)}
                                onChange={(e) =>
                                  shotPatch(shot.id, {
                                    dialogueMode: e.target.value as 'dialogue' | 'narration',
                                  })
                                }
                              >
                                <option value="dialogue">角色开口对白</option>
                                <option value="narration">画外旁白 / 内心独白</option>
                              </select>
                            </Field>
                            <Field label="对白 / 旁白">
                              <textarea
                                rows={2}
                                value={shot.dialogue}
                                onChange={(e) => shotPatch(shot.id, { dialogue: e.target.value })}
                              />
                            </Field>
                          </>
                        )}
                        {shot.staging && (
                          <ShotPlanEditor
                            shot={shot}
                            fps={fps}
                            design={draft}
                            roles={references.data?.items ?? []}
                            change={(next) => shotPatch(shot.id, syncShot(next))}
                          />
                        )}
                        {!shot.staging && (
                          <details className="shot-references">
                            <summary>
                              出场角色与参考图 ·{' '}
                              {castForShot(references.data?.items ?? [], shot).length} 位
                            </summary>
                            <ReferenceSelection
                              roles={references.data?.items ?? []}
                              ids={castForShot(references.data?.items ?? [], shot)}
                              onChange={(ids) => shotPatch(shot.id, { characterIds: ids })}
                            />
                          </details>
                        )}
                        <div className="shot-voice-direction">
                          <strong>角色声音 · 视频原声</strong>
                          {(() => {
                            const speakingIds = shot.dialogueLines
                              ? new Set(shot.dialogueLines.map((line) => line.characterId))
                              : null;
                            const speakingVoices =
                              voices.data?.items.filter((v) =>
                                speakingIds
                                  ? speakingIds.has(v.characterId)
                                  : v.name === shot.speaker,
                              ) ?? [];
                            if (shot.dialogueLines?.length === 0)
                              return <p className="quiet">本镜无对白，保留动作与环境声。</p>;
                            return speakingVoices.length ? (
                              <>
                                {speakingVoices.map((voice) => (
                                  <div key={voice.characterId}>
                                    <p>{voiceSummary(voice)}</p>
                                    <button
                                      className="button small secondary"
                                      onClick={() => setVoiceEditor(voice.characterId)}
                                    >
                                      <Volume2 size={15} />
                                      编辑{voice.name}的声音
                                    </button>
                                  </div>
                                ))}
                              </>
                            ) : (
                              <p className="quiet">
                                {voices.error?.message ??
                                  (voices.isPending
                                    ? '正在读取角色声音…'
                                    : '未绑定角色声音时由视频模型自然表达。')}
                              </p>
                            );
                          })()}
                          <small>逐句对白按说话角色继承声音；每句语气可在分镜中调整。</small>
                        </div>
                        <select
                          aria-label={`镜头 ${index + 1} 配音`}
                          value={shot.voiceId}
                          onChange={(e) => shotPatch(shot.id, { voiceId: e.target.value })}
                        >
                          <option value="">使用视频原声</option>
                          {shot.voiceId && !audio && (
                            <option value={shot.voiceId} disabled>
                              {assets.boundPending ? '正在读取已选配音…' : '已选配音暂不可用'}
                            </option>
                          )}
                          {assets.data?.items
                            .filter(
                              (a) =>
                                a.mimeType.startsWith('audio/') &&
                                !a.spec.speechLineOnly &&
                                !a.spec.speechPreviewOnly,
                            )
                            .map((a) => (
                              <option
                                key={a.id}
                                value={a.id}
                                disabled={a.spec.timingValid === false}
                              >
                                {a.name} · {a.spec.durationSeconds.toFixed(1)}s
                                {a.spec.timingValid === false ? ' · 需调整对白时间' : ''}
                              </option>
                            ))}
                        </select>
                        <button
                          className="button secondary"
                          disabled={
                            busy || dirty || (!shot.dialogue.trim() && !shot.dialogueLines?.length)
                          }
                          onClick={() => void generate('voice', shot)}
                        >
                          <Volume2 size={16} />
                          生成独立角色配音
                        </button>
                        <small className="quiet">
                          {shot.voiceId
                            ? '当前使用独立配音，替换视频全部原声；不会自动调整口型。选择“使用视频原声”可恢复。'
                            : '当前保留视频原声。独立配音生成后先试听，采用并保存后才用于合成。'}
                        </small>
                        {audio && <audio controls src={audio.url} />}
                        {audio && audio.spec.durationSeconds > shot.durationFrames / fps + 0.1 && (
                          <p className="error-text">
                            对白超出镜头{' '}
                            {(audio.spec.durationSeconds - shot.durationFrames / fps).toFixed(1)}{' '}
                            秒，请调整节奏。
                          </p>
                        )}
                        <Field label="字幕（留空时使用对白）">
                          <input
                            value={shot.caption}
                            onChange={(e) => shotPatch(shot.id, { caption: e.target.value })}
                          />
                        </Field>
                      </div>
                    </div>
                  </article>
                );
              })}
            </>
          )}
          {tab === 'assets' && (
            <>
              <div className="section-heading">
                <h2>这一集的素材架</h2>
                <label className="button primary">
                  <Upload size={16} />
                  上传素材
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,video/mp4,audio/*"
                    hidden
                    disabled={busy}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file)
                        void perform(
                          () => uploadAsset(`${base}/assets?seriesId=${project.id}`, file),
                          '素材已上传并通过格式检查',
                        );
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              <div className="info-box">
                支持图片、MP4、音频，每个文件最大 128
                MB。系统会检验实际格式和时长；画面按原比例适配，不强行裁掉人物。
              </div>
              <div className="music-editor">
                <Field label="整集背景音乐">
                  <select
                    value={draft.musicId}
                    onChange={(e) => patch({ musicId: e.target.value })}
                  >
                    <option value="">不使用背景音乐</option>
                    {draft.musicId &&
                      !assets.data.items.some((asset) => asset.id === draft.musicId) && (
                        <option value={draft.musicId} disabled>
                          {assets.boundPending ? '正在读取已选音乐…' : '已选音乐暂不可用'}
                        </option>
                      )}
                    {assets.data?.items
                      .filter((a) => a.mimeType.startsWith('audio/'))
                      .map((a) => (
                        <option value={a.id} key={a.id}>
                          {a.name}
                        </option>
                      ))}
                  </select>
                </Field>
                <Field label={`背景音乐音量 ${Math.round(draft.musicVolume * 100)}%`}>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={draft.musicVolume}
                    onChange={(e) => patch({ musicVolume: Number(e.target.value) })}
                  />
                </Field>
                <button className="button secondary" onClick={() => generate('music')}>
                  <Sparkles size={15} />
                  生成配乐
                </button>
              </div>
              <div className="asset-grid">
                {assets.data?.items.map((a) => (
                  <article className="asset-card" key={a.id}>
                    <AssetPreview asset={a} />
                    <div>
                      <strong>{a.name}</strong>
                      <small>
                        {a.mimeType}{' '}
                        {a.spec.durationSeconds > 0 && `· ${a.spec.durationSeconds.toFixed(1)} 秒`}
                      </small>
                      <button
                        className="button small secondary"
                        onClick={() => setSelectedAsset(a)}
                      >
                        查看素材
                      </button>
                      {/^(image|video)\//.test(a.mimeType) && (
                        <button
                          className="button small secondary"
                          disabled={busy}
                          onClick={() =>
                            setGenerator({
                              kind: 'qa',
                              assetId: a.id,
                              prompt:
                                '检查实际画面清晰度、构图、可见的文字与肢体异常。请先描述看到的内容，再给出有画面依据的修改建议。',
                            })
                          }
                        >
                          <Check size={14} />
                          AI 画面审查
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
              {assets.hasNextPage && (
                <button
                  className="button secondary"
                  disabled={assets.isFetchingNextPage}
                  onClick={() => void assets.fetchNextPage()}
                >
                  {assets.isFetchingNextPage ? '正在读取…' : '载入更多素材'}
                </button>
              )}
            </>
          )}
          {tab === 'delivery' && (
            <>
              <div className="section-heading">
                <h2>让故事成为成片。</h2>
              </div>
              <div className="delivery-steps">
                <article>
                  <span>01</span>
                  <h3>审阅并定稿</h3>
                  <p>冻结剧本、分镜、素材选择和下集交接。之后的修改保留为新版本。</p>
                  <button
                    className="button secondary"
                    disabled={dirty || busy}
                    onClick={() => {
                      setReviewed(false);
                      setApproval(true);
                    }}
                  >
                    <Check size={15} />
                    定稿当前版本
                  </button>
                </article>
                <article>
                  <span>02</span>
                  <h3>合成与检查</h3>
                  <p>按照时间线合成画面、配音、背景音乐和字幕，检查整片解码与时长。</p>
                  <button
                    className="button primary"
                    disabled={
                      dirty ||
                      busy ||
                      !activeCut ||
                      resultTasks.some((t) => t.kind === 'render' && pending(t))
                    }
                    onClick={() =>
                      void perform(
                        () =>
                          api(`${base}/studio-tasks`, 'POST', {
                            kind: 'render',
                            episodeId: id,
                            cutId: activeCut!.id,
                          }),
                        '合成任务已加入队列',
                      )
                    }
                  >
                    <Film size={15} />
                    合成 MP4
                  </button>
                </article>
                <article>
                  <span>03</span>
                  <h3>观看并发布</h3>
                  <p>发布会保留不可变的成片与交接，供下一集继续使用。</p>
                  <Badge tone={release ? 'green' : 'neutral'}>
                    {release ? '已发布，可开始下一集' : '等待观看成片'}
                  </Badge>
                </article>
              </div>
              {resultTasks
                .filter((t) => t.kind === 'render' && t.result.asset)
                .map((t) => (
                  <article className="render-card" key={t.id}>
                    <video controls src={t.result.asset!.url} preload="metadata" />
                    <div>
                      <h3>{draft.title || current.title}</h3>
                      <Badge tone="green">
                        解码通过 · {t.result.qa?.actualSeconds.toFixed(2)} 秒
                      </Badge>
                      <p>{new Date(t.createdAt).toLocaleString()}</p>
                      <div className="row-actions">
                        <a
                          className="button secondary"
                          href={t.result.asset!.url}
                          download={`${draft.title || current.title}.mp4`}
                        >
                          <Download size={15} />
                          下载 MP4
                        </a>
                        {!release && (
                          <button
                            className="button primary"
                            disabled={dirty || t.cutId !== activeCut?.id}
                            onClick={() => {
                              setReviewed(false);
                              setPublish(t);
                            }}
                          >
                            发布这一集
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              {release && (
                <div className="info-box">
                  <CircleCheck size={18} />
                  <span>
                    已发布：{release.handoff.summary}
                    <br />
                    下一集制作页可以载入此交接。
                  </span>
                </div>
              )}
            </>
          )}
        </section>
        <aside className="production-task-panel">
          <h3>
            制作任务{' '}
            <span>
              {resultTasks.filter(pending).length
                ? `${resultTasks.filter(pending).length} 进行中`
                : ''}
            </span>
          </h3>
          {!resultTasks.length && <p className="quiet">你的生成、配音和合成任务会出现在这里。</p>}
          {resultTasks.slice(0, 25).map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              fps={fps}
              regenerateLine={
                t.kind === 'voice' && t.shotId && t.result.speechLines
                  ? (lineIndex?: number) => {
                      void perform(
                        () =>
                          api(`${base}/studio-tasks`, 'POST', {
                            kind: 'voice',
                            episodeId: id,
                            shotId: t.shotId,
                            modelId: t.modelId,
                            reuseTaskId: t.id,
                            ...(lineIndex === undefined ? {} : { lineIndex }),
                            productionHash: doc.data!.hash,
                            confirmed: true,
                          }),
                        lineIndex === undefined
                          ? '已提交补齐缺失台词，原已成功的声音会保留'
                          : '单句配音任务已提交，其他已成功的声音会保留',
                      );
                    }
                  : undefined
              }
              speechBusy={busy || dirty || pending(t)}
              adoptBlocked={
                t.result.timingValid === false
                  ? '配音未完整通过时间检查，请按错误提示处理后重做。'
                  : t.result.proposal
                    ? dirty
                      ? '请先保存或撤销当前修改，再采用候选。'
                      : t.result.sourceDocumentHash && t.result.sourceDocumentHash !== doc.data.hash
                        ? '这份候选基于旧草稿，请先核对变化；需要基于当前版本重新生成。'
                        : undefined
                    : t.kind === 'voice' &&
                        t.result.speechLines &&
                        !speechMatchesShot(
                          t.result.speechLines,
                          draft.shots.find((shot) => shot.id === t.shotId),
                        )
                      ? '此配音基于旧制作稿，请核对台词与时间后重新生成。'
                      : undefined
              }
              onAdopt={
                t.result.proposal
                  ? () => {
                      if (t.kind === 'storyboard')
                        patch({
                          shots: structuredClone(t.result.proposal!.shots),
                          script: t.result.proposal!.script,
                          sceneDesigns: structuredClone(t.result.proposal!.sceneDesigns ?? []),
                          propDesigns: structuredClone(t.result.proposal!.propDesigns ?? []),
                        });
                      else setDraft(structuredClone(t.result.proposal!));
                      setTab(t.kind === 'storyboard' ? 'shots' : 'script');
                      toast('候选已载入编辑器，请审阅后保存');
                    }
                  : t.result.asset && t.shotId
                    ? () => {
                        if (!draft.shots.some((s) => s.id === t.shotId)) {
                          toast('原镜头已删除，素材仍保留在素材库', true);
                          return;
                        }
                        shotPatch(
                          t.shotId!,
                          t.kind === 'voice'
                            ? { voiceId: t.result.asset!.id }
                            : { visualId: t.result.asset!.id },
                        );
                        toast('素材已选择，请保存草稿');
                      }
                    : t.kind === 'music' && t.result.asset
                      ? () => patch({ musicId: t.result.asset!.id })
                      : undefined
              }
            />
          ))}
        </aside>
      </div>
      {voiceEditor && (
        <Dialog title="编辑角色声音" close={() => setVoiceEditor(null)}>
          <div className="dialog-body">
            <CharacterVoicePanel characterId={voiceEditor} branchId={current?.branchId} />
          </div>
        </Dialog>
      )}
      {generator && (
        <GenerateDialog
          episodeId={generator.episodeId ?? id!}
          kind={generator.kind}
          shotId={generator.shotId}
          assetId={generator.assetId}
          initialPrompt={generator.prompt}
          firstFrameStepId={generator.firstFrameStepId}
          voice={generator.voice}
          referenceRoles={generator.referenceRoles}
          referenceIds={generator.referenceIds}
          seconds={generator.seconds ?? 10}
          close={() => setGenerator(null)}
        />
      )}
      {approval && (
        <Dialog
          title="定稿本集制作版本"
          subtitle="将冻结当前内容和素材引用"
          close={() => setApproval(false)}
        >
          <div className="dialog-body">
            <p>
              {draft.title} · {draft.shots.length} 个镜头 · {seconds.toFixed(1)} 秒
            </p>
            <p>{draft.handoff.summary || '请先填写本集已经发生的剧情事实。'}</p>
            <label className="check-row">
              <input
                type="checkbox"
                checked={reviewed}
                onChange={(e) => setReviewed(e.target.checked)}
              />
              我已审阅剧本、分镜、人物连续性和交接内容
            </label>
            <button
              className="button primary"
              disabled={!reviewed || busy}
              onClick={() =>
                void perform(async () => {
                  await api(`${base}/episodes/${id}/production/approve`, 'POST', {
                    expectedRevision: loadedRevision,
                    contentHash: doc.data!.hash,
                    humanReviewed: true,
                  });
                  setApproval(false);
                }, '制作版本已定稿')
              }
            >
              确认定稿
            </button>
          </div>
        </Dialog>
      )}
      {publish && (
        <Dialog
          title="发布这一集"
          subtitle="发布后，下一集将引用此版本的交接"
          close={() => setPublish(null)}
        >
          <div className="dialog-body">
            <video className="release-preview" controls src={publish.result.asset?.url} />
            <label className="check-row">
              <input
                type="checkbox"
                checked={reviewed}
                onChange={(e) => setReviewed(e.target.checked)}
              />
              我已完整观看，确认画面、对白、字幕和剧情可以发布
            </label>
            <button
              className="button primary"
              disabled={!reviewed || busy}
              onClick={() =>
                void perform(async () => {
                  await api(`${base}/episodes/${id}/production/release`, 'POST', {
                    cutId: publish.cutId,
                    renderTaskId: publish.id,
                    humanReviewed: true,
                  });
                  setPublish(null);
                }, '本集已发布，交接已保存')
              }
            >
              确认发布
            </button>
          </div>
        </Dialog>
      )}
      {selectedAsset && (
        <Dialog title={selectedAsset.name} close={() => setSelectedAsset(null)}>
          <div className="dialog-body">
            <AssetPreview asset={selectedAsset} />
            <a className="button secondary" href={selectedAsset.url} download={selectedAsset.name}>
              <Download size={15} />
              下载素材
            </a>
          </div>
        </Dialog>
      )}
    </div>
  );
}
function GenerateDialog({
  episodeId,
  kind,
  shotId,
  assetId,
  initialPrompt,
  firstFrameStepId,
  voice,
  referenceRoles = [],
  referenceIds = [],
  seconds,
  close,
}: {
  episodeId: string;
  kind: string;
  shotId?: string | undefined;
  assetId?: string | undefined;
  initialPrompt: string;
  firstFrameStepId?: string | undefined;
  voice?: CharacterVoice | undefined;
  referenceRoles?: CharacterReference[] | undefined;
  referenceIds?: string[] | undefined;
  seconds: number;
  close: () => void;
}) {
  const { base, perform, busy, go, project } = useStudio();
  const policy = useApi<Schema<'ModelPolicy'>>(
    `${base}${project ? `/series/${project.id}` : ''}/model-policy`,
  );
  const models = useApi<Page<Model>>(`${base}/models`),
    settings = useApi<Settings>(`${base}/model-settings`);
  const [narratorVoiceId, setNarratorVoiceId] = useState('eve');
  const [modelId, setModelId] = useState(''),
    [prompt, setPrompt] = useState(initialPrompt);
  const [castIds, setCastIds] = useState(referenceIds);
  const [textOnly, setTextOnly] = useState(false);
  const media = ['image', 'video', 'music'].includes(kind);
  const options =
    models.data?.items.filter((m) =>
      kind === 'voice'
        ? isVoiceModel(m)
        : media
          ? m.upstreamModelId.includes(kind)
          : m.selectable && isTextModel(m),
    ) ?? [];
  const role =
    kind === 'write'
      ? 'writer'
      : kind === 'storyboard'
        ? 'storyboard_designer'
        : kind === 'qa'
          ? 'qa_reviewer'
          : kind === 'review'
            ? 'continuity_reviewer'
            : (kind as 'image' | 'music' | 'video');
  const preferred =
    kind === 'voice' ? undefined : policy.data?.bindings[role]?.primary.catalogModelId;
  const selectedId = modelId || preferred || options[0]?.catalogModelId || '';
  const selected = options.find((m) => m.catalogModelId === selectedId);
  const promptBytes = new TextEncoder().encode(prompt).length;
  const promptTooLong =
    kind === 'video' &&
    selected?.upstreamModelId.startsWith('grok-imagine-video') &&
    promptBytes > 4096;
  const selectedRefs = castIds.map((id) => referenceRoles.find((r) => r.characterId === id));
  const referenceError =
    kind === 'video' &&
    !textOnly &&
    (castIds.length > 7 ||
      selectedRefs.some((r) => !r?.asset) ||
      selectedRefs.reduce((n, r) => n + Number(r?.asset?.sizeBytes ?? 0), 0) > 32 * 1024 * 1024 ||
      ((castIds.length > 0 || !!firstFrameStepId) &&
        selected?.upstreamModelId !== 'grok-imagine-video-1.5'));
  return (
    <Dialog
      title={names[kind] ?? '生成'}
      subtitle="生成结果先保存为候选，不会覆盖你的草稿或定稿"
      close={close}
    >
      <div className="dialog-body">
        <Field label="使用模型">
          <select value={selectedId} onChange={(e) => setModelId(e.target.value)}>
            {!selected && (
              <option value={selectedId}>
                {selectedId ? '已绑定的模型不可用，请重新选择' : '暂无已启用模型'}
              </option>
            )}
            {options.map((m) => (
              <option key={m.catalogModelId} value={m.catalogModelId}>
                {m.displayName}
              </option>
            ))}
          </select>
        </Field>
        {kind !== 'voice' && (
          <Field label="创作要求">
            <textarea
              rows={7}
              maxLength={16000}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </Field>
        )}
        {kind === 'voice' && (
          <>
            <VoicePicker label="旁白音色" value={narratorVoiceId} change={setNarratorVoiceId} />
            <p className="quiet">
              按已保存分镜的逐句台词和时间生成，角色对白使用角色库已确认的音色。结果先保存为候选，试听后采用；修改台词请返回分镜编辑。
            </p>
            <p className="quiet">采用独立配音会替换视频原声（含环境音），不会自动调整人物口型。</p>
          </>
        )}
        {kind === 'video' && selected?.upstreamModelId.startsWith('grok-imagine-video') && (
          <p className={promptTooLong ? 'form-error' : 'quiet'}>
            正文 {promptBytes} 字节 / 接口总限额 4096
            字节。提交时还会附上角色声音和参考图说明，最终长度由服务器核对。中文通常每字占 3 字节。
          </p>
        )}
        {kind === 'qa' && (
          <p className="quiet">
            将发送该素材的实际图片或最多 5
            张视频静帧给所选模型。兼容网关的图片输入需实际测试；这里只检查静帧，不检查声音、完整运动或口型。
          </p>
        )}
        {media && (
          <p className="quiet">
            角色参考图用于约束外貌和服装，生成后仍需逐镜审阅人物一致性。声音按角色描述生成，当前未接入固定声纹。
          </p>
        )}
        {kind === 'video' && (
          <p>
            本次生成时长：{Math.min(15, Math.max(1, Math.ceil(seconds)))}{' '}
            秒。对白与声音由视频模型直接生成，请在提示词中核对台词，完成后试听。
          </p>
        )}
        {kind === 'video' && selected?.providerKey === 'grok2api' && (
          <p className="info-box">
            Grok2API 当前 Web
            账号只支持文字生视频，不支持首帧和角色参考图。可选择本次纯文字生成；需要人物参考图的连续短剧请选择
            CLIProxyAPI。
          </p>
        )}
        {kind === 'image' &&
          selected?.providerKey === 'grok2api' &&
          selected.upstreamModelId === 'grok-imagine-image-edit' && (
            <p className="form-error">
              这是带参考图的图片编辑模型。纯文字生图或角色封面请选择 Grok2API 的
              grok-imagine-image-lite。
            </p>
          )}
        {kind === 'video' && (
          <section className="video-reference-options">
            {firstFrameStepId && (
              <p className="info-box">
                已带入本镜批准的首帧。提示词按表演顺序连接动作、对白与听者反应，人物后的图片编号对应下方参考图。修改外观或构图请先重做关键帧；原稿中的帧数用于剪辑，视频表演仍需观看确认。
              </p>
            )}
            {!firstFrameStepId && (
              <label className="reference-text-only">
                <input
                  type="checkbox"
                  checked={textOnly}
                  onChange={(e) => setTextOnly(e.target.checked)}
                />
                本次纯文字生成，不携带角色参考图
              </label>
            )}
            {!textOnly && (
              <>
                {firstFrameStepId ? (
                  <p>
                    随首帧携带角色参考图：
                    {selectedRefs.map((r) => r?.name).join('、') || '无出场角色'}
                  </p>
                ) : (
                  <ReferenceSelection roles={referenceRoles} ids={castIds} onChange={setCastIds} />
                )}
                {castIds.length > 0 && selected?.upstreamModelId !== 'grok-imagine-video-1.5' && (
                  <p className="form-error">参考图模式请选择 grok-imagine-video-1.5。</p>
                )}
                {selectedRefs.reduce((n, r) => n + Number(r?.asset?.sizeBytes ?? 0), 0) >
                  32 * 1024 * 1024 && (
                  <p className="form-error">参考图合计超过 32 MB，请缩小图片后重新确认。</p>
                )}
                <small>每次最多 7 张，合计最多 32 MB；图片将随请求发送给所选视频模型。</small>
              </>
            )}
          </section>
        )}
        {kind === 'video' && voice && (
          <p className="shot-voice-direction">
            <strong>继承{voice.name}的角色声音</strong>
            {voiceSummary(voice)}
          </p>
        )}
        {['write', 'storyboard', 'design'].includes(kind) && (
          <p className="quiet">生成结果先保存为候选，审阅后再应用。</p>
        )}
        {!selected && (
          <button
            className="button small secondary"
            onClick={() => {
              close();
              go('models');
            }}
          >
            去配置模型
          </button>
        )}
        <button
          className="button primary"
          disabled={
            busy ||
            policy.isPending ||
            !!policy.error ||
            !selected ||
            !settings.data?.callsEnabled ||
            (kind !== 'voice' && !prompt.trim()) ||
            (kind === 'voice' && !narratorVoiceId.trim()) ||
            referenceError ||
            promptTooLong
          }
          onClick={() =>
            void perform(async () => {
              await api(`${base}/studio-tasks`, 'POST', {
                kind,
                episodeId,
                modelId: selected!.catalogModelId,
                ...(kind === 'voice' ? { narratorVoiceId } : { prompt }),
                ...(firstFrameStepId ? { firstFrameStepId } : {}),
                ...(kind === 'video'
                  ? {
                      textOnlyVideo: textOnly,
                      referenceCharacterIds: textOnly ? [] : castIds,
                      referenceHashes: textOnly ? [] : selectedRefs.map((r) => r!.hash),
                    }
                  : {}),
                ...(kind === 'video' && voice
                  ? { voiceCharacterId: voice.characterId, voiceProfileHash: voice.hash }
                  : {}),
                ...(shotId ? { shotId } : {}),
                ...(assetId ? { assetId } : {}),
                ...(kind === 'video'
                  ? { duration: Math.min(15, Math.max(1, Math.ceil(seconds))) }
                  : {}),
                ...(kind === 'video'
                  ? {
                      nativeAudio: true,
                      aspectRatio: initialPrompt.includes('9:16') ? '9:16' : '16:9',
                    }
                  : {}),
                confirmed: true,
              });
              close();
            }, '生成任务已提交')
          }
        >
          <Sparkles size={16} />
          开始{names[kind]}
        </button>
      </div>
    </Dialog>
  );
}
function AssetPreview({ asset }: { asset: Asset }) {
  return asset.mimeType.startsWith('image/') ? (
    <img src={asset.url} alt={asset.name} loading="lazy" />
  ) : asset.mimeType.startsWith('video/') ? (
    <video src={asset.url} controls preload="metadata" />
  ) : (
    <div className="audio-preview">
      <Volume2 size={30} />
      <audio src={asset.url} controls preload="metadata" />
    </div>
  );
}
export function TaskCard({
  task: t,
  onAdopt,
  adoptBlocked,
  regenerateLine,
  speechBusy = false,
  fps = 24,
}: {
  task: StudioTask;
  onAdopt?: (() => void) | undefined;
  adoptBlocked?: string | undefined;
  regenerateLine?: ((lineIndex?: number) => void) | undefined;
  speechBusy?: boolean;
  fps?: number;
}) {
  const { base, perform, busy } = useStudio();
  return (
    <article className="production-task">
      <header>
        <strong>{names[t.kind]}</strong>
        <Badge
          tone={
            t.state === 'completed' ? 'green' : t.state === 'outcome_unknown' ? 'amber' : 'neutral'
          }
        >
          {names[t.state]}
        </Badge>
      </header>
      {pending(t) && (
        <>
          <progress max="100" value={t.progress} />
          <small>{t.step ?? '任务已保存到后台，关闭页面后仍继续执行'}</small>
        </>
      )}
      {t.result.model && <small>执行模型：{t.result.model.upstreamModelId}</small>}
      {t.errorMessage && <p className="error-text">{mediaFailureMessage(t)}</p>}
      {t.result.warnings?.map((warning) => (
        <p className="form-error" key={warning}>
          {warning}
        </p>
      ))}
      {t.result.speechPartial && (
        <p className="form-error">
          部分台词尚未完成，已成功的声音已保存。继续生成会复用仍有效的声音；台词或声音设定改变时，将生成新候选。
        </p>
      )}
      {t.result.speechPartial && regenerateLine && (
        <button className="button secondary" disabled={speechBusy} onClick={() => regenerateLine()}>
          <RefreshCw size={15} />
          继续生成缺失台词
        </button>
      )}
      {t.result.speechLines && (
        <SpeechLines
          lines={t.result.speechLines}
          regenerate={t.result.speechPartial ? undefined : regenerateLine}
          busy={speechBusy}
          fps={fps}
        />
      )}
      {t.kind === 'voice' && t.result.asset && (
        <div className="speech-sample">
          <strong>整镜配音试听</strong>
          <audio controls preload="metadata" src={t.result.asset.url} aria-label="整镜配音试听" />
        </div>
      )}
      {!!t.referenceImages?.length && (
        <details>
          <summary>本次携带 {t.referenceImages.length} 张角色参考图</summary>
          <div className="reference-thumbnails">
            {t.referenceImages.map((r, i) => (
              <figure key={r.characterId}>
                <img src={r.asset!.url} alt={`${r.name}的生成时参考图`} />
                <figcaption>
                  {i + 1}. {r.name} · 第 {r.revision} 版
                </figcaption>
              </figure>
            ))}
          </div>
        </details>
      )}
      {t.referenceMode === 'text_only' && <small>本次已选择纯文字生成</small>}
      {t.result.proposal && (
        <details>
          <summary>查看{t.kind === 'storyboard' ? '分镜' : '剧本与分镜'}候选</summary>
          {t.kind !== 'storyboard' && <p className="task-text">{t.result.proposal.script}</p>}
          {t.result.proposal.shots.map((shot, index) => (
            <div className="candidate-shot" key={shot.id}>
              <strong>
                镜头 {index + 1} · {shot.durationFrames} 帧
              </strong>
              <ShotPlanView shot={shot} design={t.result.proposal ?? {}} />
              {!shot.staging && shot.dialogue && (
                <p>
                  {shot.speaker}：{shot.dialogue}
                </p>
              )}
            </div>
          ))}
          {t.kind === 'storyboard' && (
            <p className="quiet">采用会替换整组镜头，保留剧本和交接。原素材仍在素材库。</p>
          )}
        </details>
      )}
      {t.result.continuityReview && (
        <details open>
          <summary>查看有依据的连续性报告</summary>
          <p className="task-text">{t.result.continuityReview.summary}</p>
          {t.result.continuityReview.findings.map((finding, index) => (
            <section className="review-finding" key={index}>
              <Badge tone={finding.severity === 'error' ? 'amber' : 'neutral'}>
                {{ error: '问题', warning: '风险', info: '建议' }[finding.severity]}
              </Badge>
              <p>{finding.description}</p>
              <p>
                <strong>建议：</strong>
                {finding.suggestion}
              </p>
              {finding.sourceIds.map((id) => {
                const source = t.result.sources?.find((s) => s.id === id);
                return (
                  <details key={id}>
                    <summary>
                      依据：
                      {source?.label
                        ? ({
                            story_bible: '故事设定',
                            character_profile: '人物小传',
                            episode_brief: '分集简报',
                            season_outline: '系列大纲',
                          }[source.label] ?? source.label)
                        : id}
                    </summary>
                    <p className="quiet">
                      {source?.status === 'draft' ? '生成时的草稿快照' : '生成时已批准的背景'} ·{' '}
                      {id}
                    </p>
                    <pre className="task-source">{JSON.stringify(source?.content, null, 2)}</pre>
                  </details>
                );
              })}
            </section>
          ))}
          {t.result.limitations?.map((text) => (
            <p className="quiet" key={text}>
              {text}
            </p>
          ))}
        </details>
      )}

      {t.result.visualReview && (
        <details open>
          <summary>查看实际画面审查</summary>
          <p className="task-text">{t.result.visualReview.summary}</p>
          <p className="quiet">
            文件完整解码：{t.result.technical?.decode === 'pass' ? '通过' : '未通过'} · 检查{' '}
            {t.result.samples?.length ?? 0} 张静帧
          </p>
          <div className="review-samples">
            {t.result.samples?.map((sample) => (
              <figure key={sample.index}>
                <a href={sample.url} target="_blank" rel="noreferrer">
                  <img src={sample.url} alt={`检查样本 ${sample.index + 1}`} loading="lazy" />
                </a>
                <figcaption>
                  样本 {sample.index + 1}
                  {sample.atSeconds !== null
                    ? ` · ${sample.atSeconds.toFixed(2)} 秒`
                    : ' · 图片缩览'}
                </figcaption>
              </figure>
            ))}
          </div>
          {t.result.visualReview.findings.map((finding, index) => (
            <section className="review-finding" key={index}>
              <Badge tone={finding.severity === 'error' ? 'amber' : 'neutral'}>
                {{ error: '问题', warning: '风险', info: '建议' }[finding.severity]}
              </Badge>
              <p>{finding.description}</p>
              <p>
                <strong>建议：</strong>
                {finding.suggestion}
              </p>
              <small>依据样本：{finding.sampleIndices.map((i) => i + 1).join('、')}</small>
            </section>
          ))}
          {t.result.limitations?.map((text) => (
            <p className="quiet" key={text}>
              {text}
            </p>
          ))}
        </details>
      )}
      {t.result.text && (
        <details>
          <summary>查看结果</summary>
          <p className="task-text">{t.result.text}</p>
        </details>
      )}
      {t.result.asset && (
        <small>
          {t.result.asset.mimeType} ·{' '}
          {t.result.asset.spec.durationSeconds > 0
            ? t.result.asset.spec.durationSeconds.toFixed(1) + ' 秒'
            : '图片已保存'}
        </small>
      )}
      {onAdopt && (
        <button
          className="button small secondary"
          onClick={onAdopt}
          disabled={!!adoptBlocked}
          title={adoptBlocked}
        >
          采用此{t.result.proposal ? '候选' : '素材'}
        </button>
      )}
      {onAdopt && adoptBlocked && <p className="quiet">{adoptBlocked}</p>}
      {t.state === 'queued' && (
        <button
          className="button small secondary"
          disabled={busy}
          onClick={() =>
            void perform(() => api(`${base}/studio-tasks/${t.id}/cancel`, 'POST', {}), '任务已取消')
          }
        >
          取消等待
        </button>
      )}
      {t.canRecover && (
        <button
          className="button small secondary"
          disabled={busy}
          onClick={() =>
            void perform(
              () => api(`${base}/studio-tasks/${t.id}/recover`, 'POST', {}),
              '已恢复原任务，不会再次提交上游生成',
            )
          }
        >
          恢复原任务
        </button>
      )}
    </article>
  );
}
export function ProductionTaskList() {
  const { base } = useStudio();
  const tasks = useTasks(`${base}/studio-tasks`);
  return (
    <section className="production-all-tasks">
      <div className="section-heading">
        <h2>制作与模型任务</h2>
        <span className="quiet">刷新后继续保留</span>
      </div>
      <div className="task-grid">
        {tasks.data?.items.map((t) => (
          <TaskCard key={t.id} task={t} />
        ))}
      </div>
    </section>
  );
}

export function ContinuityLedger() {
  const { base, project, branch, episodes, go } = useStudio();
  const releases = useApi<Page<Release>>(
    `${base}/series/${project?.id}/production-releases?branchId=${branch?.id}`,
    !!project && !!branch,
  );
  return (
    <section className="continuity-ledger">
      <div className="section-heading">
        <h2>已发布剧集的连续性记录</h2>
        <Badge>{releases.data?.items.length ?? 0} 集</Badge>
      </div>
      {releases.data?.items.length ? (
        <div className="memory-list">
          {releases.data.items.map((r) => (
            <article className="memory-card" key={r.id}>
              <div>
                <Badge tone="green">已发布 · 人工确认</Badge>
                <span className="quiet">{new Date(r.createdAt).toLocaleDateString()}</span>
              </div>
              <h3>{episodes.find((e) => e.id === r.episodeId)?.title ?? '剧集交接'}</h3>
              {(
                [
                  ['summary', '已经发生'],
                  ['characterStates', '人物与知情范围'],
                  ['openThreads', '未解决伏笔'],
                  ['nextHook', '下集计划'],
                ] as const
              ).map(([key, label]) => (
                <div key={key}>
                  <strong>{label}</strong>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{r.handoff[key] || '未填写'}</p>
                </div>
              ))}
              <a className="text-button" href={r.asset.url} target="_blank" rel="noreferrer">
                查看来源成片
              </a>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          title="发布后，故事会在这里留下记录"
          text="每集的事实、人物状态、未解决伏笔和下集计划分别保存，生成下一集时会读取前集交接。"
          action={
            <button className="button secondary" onClick={() => go('production')}>
              进入制作与成片
            </button>
          }
        />
      )}
    </section>
  );
}

export function ConnectionEditor({ connection }: { connection: Schema<'Connection'> }) {
  const { base, perform, busy } = useStudio();
  const [open, setOpen] = useState(false),
    [url, setUrl] = useState(''),
    [key, setKey] = useState(''),
    [name, setName] = useState(''),
    [protocol, setProtocol] = useState('openai_chat'),
    [revision, setRevision] = useState('');
  const close = () => {
    setKey('');
    setOpen(false);
  };
  return (
    <>
      <button
        className="button small secondary"
        onClick={() =>
          void perform(async () => {
            const value = await api<{
              baseUrl: string;
              displayName: string;
              protocol: string;
              revision: string;
            }>(`${base}/connections/${connection.id}/configuration`);
            setUrl(value.baseUrl);
            setName(value.displayName);
            setProtocol(value.protocol);
            setRevision(value.revision);
            setKey('');
            setOpen(true);
          })
        }
      >
        配置
      </button>
      {open && (
        <Dialog
          title="配置模型连接"
          subtitle="密钥仅保存于服务端，页面不会回显已有密钥"
          close={close}
        >
          <div className="dialog-body">
            <Field label="连接名称">
              <input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field
              label="HTTPS 网关根地址"
              hint="填写根地址，不要追加 /v1/chat/completions 等接口路径。"
            >
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoComplete="off"
              />
            </Field>
            <Field label="协议">
              <select value={protocol} onChange={(e) => setProtocol(e.target.value)}>
                <option value="openai_chat">OpenAI Chat 兼容</option>
                <option value="openai_responses">OpenAI Responses</option>
                <option value="anthropic_messages">Anthropic Messages</option>
                <option value="gemini_native">Gemini 原生</option>
              </select>
            </Field>
            <Field
              label="API 密钥"
              hint="留空保留原密钥。Gemini 网页网关的 Cookie 在网关侧维护，这里填写网关 API 密钥。"
            >
              <input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <p className="quiet">
              修改地址、密钥或协议后需重新测试。仅修改名称或重复保存，不影响已启用模型。
            </p>
            <button
              className="button primary"
              disabled={busy || !url.startsWith('https://') || !name.trim()}
              onClick={() =>
                void perform(async () => {
                  await api(`${base}/connections/${connection.id}/configuration`, 'PUT', {
                    expectedRevision: revision,
                    displayName: name,
                    baseUrl: url,
                    protocol,
                    ...(key ? { apiKey: key } : {}),
                  });
                  close();
                }, '连接已保存；如修改了地址、密钥或协议，请重新测试模型')
              }
            >
              保存连接
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
